import React, { useCallback, useMemo, useState } from 'react';
import SearchPanel from '@components/SearchPanel';
import CheckboxCardList from '@components/CheckboxCardList';
import { translate } from '@utils/i18n';
import { useQuestionSetComponentTypes, useUpdateQuestionSetComponentTypesMutation } from '../../hooks/useQuestionSetComponentTypes';
import './ComponentTypesTabStyles.scss';
const namespace = 'features.settings.component_questions';
export default function ComponentTypesTab({ questionSetId }) {
    const [searchQuery, setSearchQuery] = useState('');
    // Fetch component types for this question set
    const { data: componentTypes = [], isLoading } = useQuestionSetComponentTypes(questionSetId);
    const updateComponentTypesMutation = useUpdateQuestionSetComponentTypesMutation();
    const handleSearchChange = useCallback((value) => {
        setSearchQuery(value);
    }, []);
    const handleCheckboxChange = useCallback((componentTypeId, isChecked) => {
        // Get current associated component type IDs
        const currentAssociatedIds = componentTypes.filter((ct) => ct.associated).map((ct) => ct.id);
        // Update the list based on checkbox change
        const updatedIds = isChecked
            ? [...currentAssociatedIds, Number(componentTypeId)]
            : currentAssociatedIds.filter((id) => id !== Number(componentTypeId));
        // Call mutation to update associations
        updateComponentTypesMutation.mutate({
            questionSetId,
            componentTypeIds: updatedIds
        });
    }, [componentTypes, questionSetId, updateComponentTypesMutation]);
    // Filter component types based on search query
    const filteredComponentTypes = useMemo(() => {
        if (!searchQuery.trim()) {
            return componentTypes;
        }
        const query = searchQuery.toLowerCase();
        return componentTypes.filter((ct) => ct.typeName.toLowerCase().includes(query) || (ct.description && ct.description.toLowerCase().includes(query)));
    }, [componentTypes, searchQuery]);
    // Convert component types to CheckboxCardItem format
    const cardItems = useMemo(() => {
        return filteredComponentTypes.map((ct) => ({
            id: ct.id,
            title: ct.typeName,
            description: ct.description || undefined,
            checked: ct.associated
        }));
    }, [filteredComponentTypes]);
    const componentTypesContent = useMemo(() => {
        if (isLoading) {
            return (React.createElement("div", { className: "component-types-tab__loading" },
                React.createElement("i", { className: "fa fa-spinner fa-spin" }),
                " ",
                translate('loading', { namespace: 'features.commons' }),
                "..."));
        }
        if (filteredComponentTypes.length === 0) {
            return (React.createElement("div", { className: "component-types-tab__empty-state" },
                React.createElement("p", null, searchQuery
                    ? translate('no_component_types_found', { namespace })
                    : translate('no_component_types', { namespace }))));
        }
        return (React.createElement(CheckboxCardList, { items: cardItems, onChange: handleCheckboxChange, className: "component-types-tab__grid", idPrefix: "component-type" }));
    }, [isLoading, cardItems, searchQuery, handleCheckboxChange]);
    return (React.createElement("div", { className: "component-types-tab" },
        React.createElement("div", { className: "component-types-tab__header" },
            React.createElement(SearchPanel, { placeholder: translate('search_component_types_placeholder', { namespace }), onChange: handleSearchChange })),
        componentTypesContent));
}
